/**
 * The opener and refocus contract behind the drawers (AIC-318, from the Results evidence sheet's opener contract, AIC-257):
 * closing a panel sends focus back to the control that opened it, or to a fallback when that control has gone (it was
 * re-rendered or removed while the panel was open).
 *
 * The panel itself is components/Drawer.tsx (AIC-503). Lightboxes are something else: short consequential decisions go
 * through the native modal ConfirmDialog, which blocks the page. A dialog opened from a drawer owns Esc while it is open.
 *
 * - canReturnTo / firstFocusable / pageFallback: where focus can go back to.
 * - usePanelOpener: the opener and refocus logic on its own (the Assignment drawers and the Results sheet use it).
 */
import { useMemo, useRef } from "react";
import "./Panel.css";

/**
 * Where focus can go back to when a panel closes: a connected element that takes focus. Not <body>, which is what
 * Safari leaves focused after a click on a button (it doesn't focus buttons on click), nor a plain container.
 */
export const canReturnTo = (el: Element | null | undefined): el is HTMLElement =>
  el instanceof HTMLElement && el !== document.body && el.isConnected && (el.tabIndex >= 0 || el.hasAttribute("tabindex")) && !el.closest("details:not([open])");

/**
 * The first of these that focus can land on: connected, and not hidden inside a closed <details> (a row that moved into
 * the materials' "Other steps"). Used for every focus fallback on the Assignment page.
 */
export const firstFocusable = (...els: readonly (Element | null | undefined)[]): HTMLElement | null => els.find(canReturnTo) ?? null;

/** The Assignment page's last resorts, in order: the next-step panel's heading, then the materials card. */
export const pageFallback = (): HTMLElement | null =>
  firstFocusable(document.getElementById("next-label"), document.getElementById("ad-materials"));

export interface PanelOpener {
  /**
   * Remembers the control a panel is opened from: `from` when the opener passes itself (Safari doesn't focus a button
   * on click), else the focused element. Only when the panel isn't already open (switching what an open panel shows
   * keeps the first opener) and never a control inside the panel itself.
   */
  remember(from: HTMLElement | null | undefined, { open, inside }: { open: boolean; inside?: HTMLElement | null }): void;
  /** Forgets the opener and hands it over, when it can still take focus (null when it has gone). */
  take(): HTMLElement | null;
  /**
   * Forgets the opener and, once the panel has closed (after this render), focuses it; when it can't take focus any
   * more, whatever `fallback` finds then.
   */
  restore(fallback?: () => HTMLElement | null | undefined): void;
}

export function usePanelOpener(): PanelOpener {
  const opener = useRef<HTMLElement | null>(null);
  return useMemo<PanelOpener>(() => {
    const take = () => {
      const back = canReturnTo(opener.current) ? opener.current : null;
      opener.current = null;
      return back;
    };
    return {
      remember(from, { open, inside }) {
        const active = canReturnTo(from) ? from : document.activeElement;
        if (!open && canReturnTo(active) && !inside?.contains(active)) opener.current = active;
      },
      take,
      restore(fallback) {
        const back = take();
        setTimeout(() => (back ?? fallback?.() ?? null)?.focus(), 0);
      },
    };
  }, []);
}
